import React, { useEffect, useRef, useState } from 'react';
import { company } from '../data/content';
import { CheckIcon, ArrowRightIcon, PhoneIcon, PlayIcon } from './icons';
interface JobVideo {
  id: string;
  label: string;
  blurb: string;
  src: string;
  silent?: boolean;
}
const videos: JobVideo[] = [{
  id: 'crew',
  label: 'On the job',
  blurb: 'Our crew working a pool from prep through finish.',
  src: 'https://cdn.magicpatterns.com/uploads/6D372fRnd4qVVDpSeurpcB/846e8e09-4c30-45c3-b00d-8d1ef499cc8a%20(1).mov'
}, {
  id: 'finished',
  label: 'Finished backyard',
  blurb: 'A completed A&J pool the day we handed it back.',
  src: 'https://cdn.magicpatterns.com/uploads/cM1hVwFjJMVgea9owMWBaC/44e71604-3288-41d9-9cce-2a4b2f41eb3c.MP4',
  silent: true
}];
const points = ['The same crew from estimate to final walkthrough', 'Prep done right before a single bag of plaster opens', 'Job site cleaned up at the end of every day'];
export function VideoSpotlight() {
  const [activeId, setActiveId] = useState(videos[0].id);
  const active = videos.find(video => video.id === activeId) ?? videos[0];
  const videoRef = useRef<HTMLVideoElement>(null);

  // Keep the silent clip muted even if a viewer tries to unmute it.
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !active.silent) return;
    el.muted = true;
    const enforceMute = () => {
      el.muted = true;
    };
    el.addEventListener('volumechange', enforceMute);
    return () => el.removeEventListener('volumechange', enforceMute);
  }, [active]);
  return <section id="video" className="w-full bg-white py-20 lg:py-28" data-id="element-726" data-mp="zsfboz0kf9ir">
      <div className="mx-auto max-w-content px-5 lg:px-8" data-id="element-727" data-mp="uxtatps40zcx">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-14" data-id="element-728" data-mp="qyaqow844sb5">
          <div data-id="element-729" data-mp="1ng71n5f13l2i">
            <div className="overflow-hidden rounded-lg bg-navy-900" data-id="element-730" data-mp="1ngp2yggjspha">
              <video key={active.src} ref={videoRef} src={active.src} controls playsInline muted={Boolean(active.silent)} loop preload="metadata" className="h-full max-h-[560px] w-full object-cover" data-id="element-731" data-mp="1ip72rumcmsxd">
                Your browser can&apos;t play this video.{' '}
                <a href={active.src} data-id="element-732" data-mp="385au2lspjn1z">Download it instead.</a>
              </video>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2" role="group" aria-label="Choose a video" data-id="element-733" data-mp="2u59q2xh5bzia">
              {videos.map(video => {
              const isActive = video.id === active.id;
              return <button key={video.id} type="button" onClick={() => setActiveId(video.id)} aria-pressed={isActive} className={`flex items-center gap-3 rounded-md border p-3 text-left transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400 ${isActive ? 'border-navy-800 bg-navy-800' : 'border-navy-200 bg-white hover:border-navy-400'}`} data-id="element-734" data-mp="2p6oniref93qo">
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${isActive ? 'bg-sky-400 text-navy-900' : 'bg-navy-50 text-navy-600'}`} data-id="element-735" data-mp="2cve99xb2uglm">
                      <PlayIcon className="h-4 w-4" data-id="element-736" data-mp="30mtd5uii55yz" />
                    </span>
                    <span className="min-w-0" data-id="element-737" data-mp="2vnq8ib6fp1rj">
                      <span className={`block font-display text-[15px] font-bold leading-tight ${isActive ? 'text-white' : 'text-navy-800'}`} data-id="element-738" data-mp="2vnzzo237teyt">
                        {video.label}
                      </span>
                      <span className={`mt-0.5 block text-[13px] leading-snug ${isActive ? 'text-navy-200' : 'text-navy-400'}`} data-id="element-739" data-mp="12qcuee7je74u">
                        {video.blurb}
                      </span>
                    </span>
                  </button>;
            })}
            </div>
          </div>

          <div data-id="element-740" data-mp="jy0jv46qbzjm">
            <p className="text-sm font-semibold text-sky-600" data-id="element-741" data-mp="oz7r924ymwpr">See it in motion</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight tracking-tight text-navy-800 sm:text-4xl" data-id="element-742" data-mp="ox3q2ryg4bt6">
              Watch a real A&amp;J job, not a sales pitch.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-navy-500" data-id="element-743" data-mp="yqu9hifptw20">
              Photos show the finish. These clips show how it gets there — our crew, our tools, and
              the kind of care we put into a backyard before we hand it back to the family.
            </p>

            <ul className="mt-7 space-y-3" data-id="element-744" data-mp="2ji6vcqjyyre9">
              {points.map(point => <li key={point} className="flex items-start gap-3 text-[15px] text-navy-600" data-id="element-745" data-mp="2fhwg0naaeomq">
                  <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-sky-600" data-id="element-746" data-mp="2ah0fmjp59km2" />
                  {point}
                </li>)}
            </ul>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-747" data-mp="20jvpy9w3b70i">
              <a href="#contact" className="inline-flex items-center justify-center gap-2 rounded-md bg-navy-800 px-6 py-3.5 font-display text-[15px] font-bold text-white transition-colors duration-150 ease-out hover:bg-navy-700" data-id="element-748" data-mp="1vtfaylcmfdc1">
                Get my free estimate
                <ArrowRightIcon className="h-4 w-4" data-id="element-749" data-mp="1htct2z4bcjws" />
              </a>
              <a href={company.phoneHref} className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-sky-600 transition-colors duration-150 ease-out hover:text-sky-500" data-id="element-750" data-mp="1hvgxrja1dmy4">
                <PhoneIcon className="h-4 w-4" data-id="element-751" data-mp="186aabgp3m53y" />
                {company.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}